import React, { useEffect, useState } from "react"
import { Box, Divider } from "@mui/material"
import FileBrowser from "./FileBrowser"
import { MultilangJob, MultiLangJobStatus, getMultilangJobs, MultilangJobFilters } from "src/services/api/document"
import { useTranslation } from "react-i18next"
import { useInterval } from "src/utils/useInterval"
import { RequestError } from "src/models/errors"
import { useLogger } from "src/providers"

interface MyFilesGridProps {
  setMultilangJob: (multilangJob?: MultilangJob) => void
  filters: MultilangJobFilters
  onOpenEmailModal: () => void
  updatedJob?: MultilangJob
}

const patchJobs = (jobs: MultilangJob[] | undefined, updatedJob: MultilangJob) =>
  jobs?.map((job) => (job.id === updatedJob.id ? { ...job, externalProjectId: updatedJob.externalProjectId } : job))

const MyFilesGrid: React.FC<MyFilesGridProps> = ({ setMultilangJob, filters, onOpenEmailModal, updatedJob }) => {
  const { t } = useTranslation("common")
  const { logger } = useLogger()

  const [quotedJobs, setQuotedJobs] = useState<MultilangJob[]>()
  const [quotedTotalCount, setQuotedTotalCount] = useState<number>(0)
  const [quotedActivePage, setQuotedActivePage] = useState<number>(0)

  const [inProgressJobs, setInProgressJobs] = useState<MultilangJob[]>()
  const [inProgressTotalCount, setInProgressTotalCount] = useState<number>(0)
  const [inProgressActivePage, setInProgressActivePage] = useState<number>(0)

  const [finishedJobs, setFinishedJobs] = useState<MultilangJob[]>()
  const [finishedTotalCount, setFinishedTotalCount] = useState<number>(0)
  const [finishedActivePage, setFinishedActivePage] = useState<number>(0)

  const handleError = (e: any) => {
    if (e instanceof RequestError && e.status !== 403) {
      logger.error("System - Failed to fetch jobs", e)
    }
  }

  const fetchQuotedData = () =>
    getMultilangJobs({
      sortBy: "uploadDate",
      sortDirection: "desc",
      offset: quotedActivePage * 6,
      limit: 6,
      ...filters,
      statuses: [MultiLangJobStatus.QUOTED, MultiLangJobStatus.QUOTING].join(",")
    })
      .then(({ totalCount, results }) => {
        setQuotedTotalCount(totalCount)
        setQuotedJobs(results)
      })
      .catch(handleError)

  const fetchInProgressData = () =>
    getMultilangJobs({
      sortBy: "uploadDate",
      sortDirection: "desc",
      offset: inProgressActivePage * 6,
      limit: 6,
      ...filters,
      statuses: [MultiLangJobStatus.IN_PROGRESS].join(",")
    })
      .then(({ totalCount, results }) => {
        setInProgressTotalCount(totalCount)
        setInProgressJobs(results)
      })
      .catch(handleError)

  const fetchFinishedData = () =>
    getMultilangJobs({
      sortBy: "deliveryDate",
      sortDirection: "desc",
      offset: finishedActivePage * 6,
      limit: 6,
      ...filters,
      statuses: [MultiLangJobStatus.FINISHED].join(",")
    })
      .then(({ totalCount, results }) => {
        setFinishedTotalCount(totalCount)
        setFinishedJobs(results)
      })
      .catch(handleError)

  const refresh = async () => {
    await fetchInProgressData()
    await fetchQuotedData()
    await fetchFinishedData()
  }

  useEffect(() => {
    fetchInProgressData()
  }, [inProgressActivePage, filters])
  useEffect(() => {
    fetchQuotedData()
  }, [quotedActivePage, filters])
  useEffect(() => {
    fetchFinishedData()
  }, [finishedActivePage, filters])
  useInterval(refresh, 10000)

  useEffect(() => {
    setQuotedActivePage(0)
    setInProgressActivePage(0)
    setFinishedActivePage(0)
  }, [filters])

  useEffect(() => {
    if (!updatedJob) return
    setQuotedJobs((jobs) => patchJobs(jobs, updatedJob))
    setInProgressJobs((jobs) => patchJobs(jobs, updatedJob))
    setFinishedJobs((jobs) => patchJobs(jobs, updatedJob))
  }, [updatedJob])

  const showFinished = filters.statuses?.includes(MultiLangJobStatus.FINISHED) || !filters.statuses
  const showInProgress = filters.statuses?.includes(MultiLangJobStatus.IN_PROGRESS) || !filters.statuses
  const showQuoted = filters.statuses?.includes(MultiLangJobStatus.QUOTED) || !filters.statuses

  return (
    <Box sx={{ py: { md: 4 }, flex: 1, minHeight: 0, overflow: "auto" }}>
      {showQuoted && (
        <>
          <FileBrowser
            browserTitle={t("files.QUOTED")}
            jobs={quotedJobs}
            showDeadline
            totalCount={quotedTotalCount}
            refresh={refresh}
            setMultilangJob={setMultilangJob}
            setActivePage={setQuotedActivePage}
            onOpenEmailModal={onOpenEmailModal}
          />
          {(showInProgress || showFinished) && <Divider sx={{ marginTop: "50px", marginBottom: "50px" }} />}
        </>
      )}
      {showInProgress && (
        <>
          <FileBrowser
            browserTitle={t("files.IN_PROGRESS")}
            jobs={inProgressJobs}
            showDeadline
            totalCount={inProgressTotalCount}
            refresh={refresh}
            setMultilangJob={setMultilangJob}
            setActivePage={setInProgressActivePage}
            onOpenEmailModal={onOpenEmailModal}
          />
          {showFinished && <Divider sx={{ marginTop: "50px", marginBottom: "50px" }} />}
        </>
      )}
      {showFinished && (
        <FileBrowser
          browserTitle={t("files.completed")}
          jobs={finishedJobs}
          showDownloadButton
          totalCount={finishedTotalCount}
          refresh={refresh}
          setMultilangJob={setMultilangJob}
          setActivePage={setFinishedActivePage}
          onOpenEmailModal={onOpenEmailModal}
        />
      )}
    </Box>
  )
}

export default MyFilesGrid
